/**
 * Review, then queue (batch 8 T12c1, #3033).
 *
 *  1. The user picks a channel and asks for a preview. The server builds the frozen document and sends nothing.
 *  2. The screen shows exactly what the server returned: the recipient, the order, the content and the document version. The
 *     frozen request binding (a hash of the document) is internal: it is sent, never shown; people review the document, not a code.
 *  3. The user ticks that they reviewed it and queues it. The request is exactly the reviewed binding, the same channel and a new
 *     operation key: nothing is rebuilt, nothing is re-reviewed on the user's behalf. The request is only sent after its exact
 *     operation key and body were saved in the browser and read back; if that fails, nothing is sent.
 *
 * Any relevant change drops the review: another channel, another order, a changed order revision, a settings save or a queued
 * dispatch on this page, a new owner or a change of rights (the boundary remounts). A `stale_review` answer drops it too and asks for a
 * new review; it is never regenerated and approved quietly. A successful answer is shown as QUEUED: nothing has been sent yet.
 *
 * What a review is, and what it can never become again. A review is identified by a generation, not by the values it was taken under:
 * every change of channel, revision, opening and closing starts a new generation, and an answer (a success OR an error) that belongs
 * to an older one is dropped on arrival. Comparing values would revive an old review when a value comes back (email, supplier's
 * system, email again). The confirmation binds exactly what is on screen: the displayed channel, the document, the order and the live
 * session (owner and generation, which the preview carries and the hook checks again).
 *
 * Every public entry point is its own boundary: the exported dialog drops everything on another tenant, user, session, rights, lock,
 * order or opening, whether or not a panel hosts it. Inside the panel the boundary adds nothing.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import {
  useDispatchAccess,
  useEnqueueDispatch,
  usePreviewDispatch,
  type ReviewedPreview,
} from '@/hooks/useProcurementDispatch';
import { DispatchDocumentView } from './DispatchDocumentView';
import { DispatchOwnedBoundary } from './DispatchOwnedBoundary';
import { DispatchStatusChip } from './DispatchStatus';
import { PendingEnqueueNotice } from './PendingEnqueueNotice';
import { apiUnavailableReason, describeDispatchError } from './dispatchErrors';
import { isStaleReview } from './dispatchOutcome';
import { useOpenSession } from './dispatchLifecycle';
import { useDocumentEpoch } from './dispatchLocalState';
import { ownerEquals } from './dispatchSession';
import {
  DISPATCH_CHANNELS,
  type DispatchChannel,
  type DispatchEnqueueResult,
} from './dispatchTypes';

export interface NewDispatchDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  /** Preselects the channel (for example from the supplier's order mode). The user still picks and reviews. */
  defaultChannel?: DispatchChannel | undefined;
  /** Changes whenever the purchase order document may have changed (for example its `updatedAt`). A review taken before is dropped. */
  documentRevision?: string | number | undefined;
  /** The server queued the dispatch. Queued only: nothing has been sent. */
  onQueued?: ((result: DispatchEnqueueResult) => void) | undefined;
  /** Opens the details of a dispatch (for a user who cannot list them). */
  onOpenDetails?: ((dispatchId: string) => void) | undefined;
  onCheckList?: (() => void) | undefined;
}

type Notice = 'stale_review' | 'not_stored' | null;

export function NewDispatchDialog(props: NewDispatchDialogProps) {
  const session = useOpenSession(props.open);
  return (
    <DispatchOwnedBoundary>
      <NewDispatchDialogBody key={`${props.purchaseOrderId}:${session}`} {...props} />
    </DispatchOwnedBoundary>
  );
}

function NewDispatchDialogBody({
  open,
  onClose,
  purchaseOrderId,
  defaultChannel,
  documentRevision,
  onQueued,
  onOpenDetails,
  onCheckList,
}: NewDispatchDialogProps) {
  const { t } = useTranslation('procurementDispatch');
  const { t: tc } = useTranslation('common');
  const access = useDispatchAccess();
  const epoch = useDocumentEpoch(access.owner);
  const previewCommand = usePreviewDispatch();
  const enqueue = useEnqueueDispatch(purchaseOrderId);

  const [channel, setChannel] = useState<DispatchChannel | null>(defaultChannel ?? null);
  const [review, setReview] = useState<{
    preview: ReviewedPreview;
    revision: string | number | undefined;
    generation: number;
  } | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [queued, setQueued] = useState<DispatchEnqueueResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The generation of the current review context. The ref is what a late answer is checked against (always current); the state makes
  // a review of an older generation read as outdated in the render that sees the change.
  const live = useRef(0);
  const [generation, setGeneration] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      live.current += 1;
    };
  }, []);

  const resetReview = () => {
    setReview(null);
    setConfirmed(false);
  };
  /** Ends the current context: whatever is still on its way no longer belongs to anything on screen. */
  const newGeneration = () => {
    live.current += 1;
    setGeneration(live.current);
    setPreviewing(false);
    setSubmitting(false);
  };

  // A new revision of the order, or a closed dialog, starts over. Not equality of values: a revision that comes back is not the review.
  const seenRevision = useRef(documentRevision);
  useLayoutEffect(() => {
    if (seenRevision.current === documentRevision) return;
    seenRevision.current = documentRevision;
    newGeneration();
  }, [documentRevision]);
  useLayoutEffect(() => {
    if (open) return;
    newGeneration();
    resetReview();
    setError(null);
    setNotice(null);
    setQueued(null);
  }, [open]);

  const preview = review?.preview ?? null;
  // Any relevant change makes the review a review of something that is no longer what the server would send, or of someone else's.
  const outdated =
    review !== null &&
    preview !== null &&
    (review.generation !== generation ||
      preview.documentEpoch !== epoch ||
      review.revision !== documentRevision ||
      preview.purchaseOrderId !== purchaseOrderId ||
      preview.channel !== channel ||
      !ownerEquals(preview.owner, access.owner) ||
      preview.generation !== access.epoch);
  const canConfirm =
    preview !== null &&
    !outdated &&
    confirmed &&
    !submitting &&
    !enqueue.isPending &&
    enqueue.held === null;
  const pendingElsewhere = enqueue.held !== null || enqueue.isPending;

  const pickChannel = (next: DispatchChannel) => {
    setChannel(next);
    // Another channel is another document: the earlier review does not apply, and neither does an answer still on its way for it.
    newGeneration();
    resetReview();
    setError(null);
    setNotice(null);
  };

  const requestPreview = async () => {
    if (channel === null || !access.canDispatch) return;
    newGeneration();
    resetReview();
    setError(null);
    setNotice(null);
    const mine = live.current;
    setPreviewing(true);
    const result = await previewCommand.run(purchaseOrderId, channel);
    // Success and failure alike: an answer of an older generation (another channel, a closing, a new revision) shows nothing.
    if (!mounted.current || live.current !== mine) return;
    setPreviewing(false);
    if (result.status === 'success')
      setReview({ preview: result.result, revision: documentRevision, generation: mine });
    else if (result.status === 'refused' || result.status === 'unknown') setError(result.error);
  };

  const queue = async () => {
    if (preview === null || review === null || !canConfirm) return;
    const mine = live.current;
    setSubmitting(true);
    setError(null);
    const result = await enqueue.submit(preview);
    // A real queueing is real whatever happened to the screen meanwhile: the owner of the page is told. Nothing else of it is shown
    // to a context that is no longer the one the user confirmed in.
    if (result.status === 'success') onQueued?.(result.result);
    if (!mounted.current || live.current !== mine) return;
    setSubmitting(false);
    if (result.status === 'success') {
      resetReview();
      setQueued(result.result);
    } else if (result.status === 'refused') {
      // The reviewed document is never regenerated on the user's behalf: the review is dropped and must be done again.
      resetReview();
      if (isStaleReview(result.error)) setNotice('stale_review');
      else setError(result.error);
    } else if (result.status === 'not_durable') {
      // Nothing was sent. The review still describes what the server would send, but the confirmation is the user's to give again.
      setConfirmed(false);
      setNotice('not_stored');
    } else if (result.status === 'uncertain') {
      resetReview();
    } else if (result.status === 'outdated') {
      // Nothing was sent; the screen already says the review is outdated.
    }
  };

  const close = () => {
    newGeneration();
    resetReview();
    setError(null);
    setNotice(null);
    setQueued(null);
    onClose();
  };

  const unavailableReason = apiUnavailableReason(error);

  return (
    <ResponsiveDialog
      open={open}
      onClose={close}
      maxWidth="md"
      fullWidth
      mobileTitle={t('new.title')}
      aria-label={t('new.title')}
    >
      <DialogHeader title={t('new.title')} onClose={close} />
      <DialogContent>
        <Stack spacing={2}>
          {queued !== null ? (
            <Alert severity="success" role="status">
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('new.queuedTitle')}
                  </Typography>
                  <DispatchStatusChip status={queued.status} />
                </Box>
                <Typography variant="body2">
                  {queued.status === 'queued' ? t('new.queuedBody') : t('new.queuedOther')}
                </Typography>
                {queued.replayed && <Typography variant="body2">{t('new.replayed')}</Typography>}
                {onOpenDetails && (
                  <Box>
                    <Button size="small" onClick={() => onOpenDetails(queued.dispatchId)}>
                      {t('new.openDetails')}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Alert>
          ) : !access.canDispatch ? (
            <Alert severity="info">{t('panel.noAccess')}</Alert>
          ) : pendingElsewhere || enqueue.journalStatus === 'corrupt' ? (
            <>
              <Typography variant="body2">{t('new.blockedByPending')}</Typography>
              <PendingEnqueueNotice enqueue={enqueue} onCheckList={onCheckList} />
            </>
          ) : (
            <>
              <Typography variant="body2">{t('new.intro')}</Typography>
              {enqueue.journalStatus === 'unavailable' && (
                <Alert severity="warning">{t('pending.storageUnavailable')}</Alert>
              )}

              <FormControl>
                <FormLabel id="new-dispatch-channel">{t('new.channel')}</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="new-dispatch-channel"
                  value={channel ?? ''}
                  onChange={(event) => pickChannel(event.target.value as DispatchChannel)}
                >
                  {DISPATCH_CHANNELS.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio disabled={submitting} />}
                      label={t(`channel.${value}`)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <Box>
                <Button
                  variant="outlined"
                  onClick={() => void requestPreview()}
                  disabled={channel === null || previewing || submitting}
                  startIcon={previewing ? <CircularProgress size={16} /> : undefined}
                >
                  {preview === null && !outdated ? t('new.preview') : t('new.previewAgain')}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {t('new.previewSendsNothing')}
                </Typography>
              </Box>

              {notice === 'stale_review' && (
                <Alert severity="warning" role="alert">
                  <Typography variant="body2" fontWeight={600}>
                    {t('new.staleTitle')}
                  </Typography>
                  <Typography variant="body2">{t('new.staleBody')}</Typography>
                </Alert>
              )}
              {notice === 'not_stored' && (
                <Alert severity="error" role="alert">
                  <Typography variant="body2" fontWeight={600}>
                    {t('new.notStoredTitle')}
                  </Typography>
                  <Typography variant="body2">{t('new.notStoredBody')}</Typography>
                </Alert>
              )}
              {error !== null && (
                <Alert severity="error" role="alert">
                  <Typography variant="body2">{describeDispatchError(t, error)}</Typography>
                  {unavailableReason !== null && (
                    <Typography variant="body2">
                      {t(`new.apiReason.${unavailableReason}`, {
                        defaultValue: t('new.apiReason.unknown'),
                      })}
                    </Typography>
                  )}
                </Alert>
              )}

              {outdated && (
                <Alert severity="warning" role="alert">
                  <Typography variant="body2" fontWeight={600}>
                    {t('new.outdatedTitle')}
                  </Typography>
                  <Typography variant="body2">{t('new.outdatedBody')}</Typography>
                </Alert>
              )}

              {preview !== null && !outdated && (
                <Stack spacing={2} component="section" aria-label={t('new.reviewLabel')}>
                  <Alert severity="info">
                    <Typography variant="body2" fontWeight={600}>
                      {t('new.reviewVersion', { version: preview.documentVersion })}
                    </Typography>
                    <Typography variant="body2">{t('new.reviewVersionHint')}</Typography>
                  </Alert>
                  <DispatchDocumentView snapshot={preview.snapshot} rendered={preview.rendered} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        disabled={submitting}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                    }
                    label={t('new.confirmReviewed')}
                  />
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{tc('close')}</Button>
        {queued === null && !pendingElsewhere && enqueue.journalStatus !== 'corrupt' && (
          <Button variant="contained" onClick={() => void queue()} disabled={!canConfirm}>
            {submitting ? t('new.queuing') : t('new.queue')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
